import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useInquiryQueueCount from '@/hooks/useInquiryQueueCount';
import useQueueAlert from '@/hooks/useQueueAlert';

/**
 * Chimes, notifies and marks the tab title when the inquiry queue grows. Renders nothing.
 *
 * Mounted once at the app root rather than inside the management area, for two reasons: a
 * secretary is not sitting on the inquiries screen when an inquiry arrives - that is the
 * whole point of an alert - and the badge hook it would otherwise live in runs once per
 * sidebar item, which would chime once per item.
 *
 * Does nothing for a user who cannot reach the inquiries screen; useInquiryQueueCount
 * decides that, and its query is disabled for them.
 */
export default function InquiryQueueAlert() {
  const { t } = useTranslation();
  const { count, lastUserMessageAt, canAccess } = useInquiryQueueCount();

  const buildNotificationBody = useCallback(
    (waiting: number) => t('inquiries.alerts.notificationBody', { count: waiting }),
    [t],
  );

  useQueueAlert({
    count,
    lastUserMessageAt,
    storageScope: 'office',
    ownsTabTitle: true,
    enabled: canAccess,
    notificationTitle: t('inquiries.alerts.notificationTitle'),
    buildNotificationBody,
  });

  return null;
}
